import {ApiService} from "../../lib/http.service";
import {programUrlPrefix, resultsUrlPrefix} from "../../constants/global.vars";
import {getDailyRaces} from "features/bulletin/bulletin.store";
import {NormalizedLeg, parseCardDate, rowHippodromeKeyword, toProgramDate} from "./ticket-legs";

// Bilet detayının AĞ katmanı. Bacak biçimlerinin tek modele indirgenmesi (ağ bağımlılığı
// olmayan, bu yüzden ayrı test edilebilen kısım) `ticket-legs.ts`te.
export * from "./ticket-legs";

const OfficialResults: Record<string, any> = {};
const officialResultsInFlight: Record<string, Promise<void>> = {};

export const ensureOfficialResults = async (dates: string[]): Promise<void> => {
    const missing = Array.from(new Set(dates)).filter((d) => !OfficialResults[d]);
    if (missing.length === 0) {
        return;
    }
    await Promise.all(missing.map((date) => {
        if (!officialResultsInFlight[date]) {
            officialResultsInFlight[date] = (async () => {
                const apiUrl = `${resultsUrlPrefix}/${toProgramDate(date)}/`;
                const result: any = await new ApiService().start("get", apiUrl, null, false);
                if (result?.status) {
                    OfficialResults[date] = result.data;
                } else {
                    console.warn(`[TicketResolve] ${date} resmî sonuçları alınamadı (status=false)`, result);
                }
            })().catch((err) => {
                console.warn(`[TicketResolve] ${date} resmî sonuçları alınırken hata`, err);
            }).finally(() => {
                // Başarısız deneme kalıcı önbelleklenmiyor; kayıt silinsin ki tekrar denensin.
                delete officialResultsInFlight[date];
            });
        }
        return officialResultsInFlight[date];
    }));
};

const programByDate: Record<string, any[]> = {};
const programInFlight: Record<string, Promise<any[] | null>> = {};

function fetchProgramForDate(programDate: string): Promise<any[] | null> {
    if (programByDate[programDate]) {
        return Promise.resolve(programByDate[programDate]);
    }
    if (!programInFlight[programDate]) {
        programInFlight[programDate] = (async () => {
            const result: any = await new ApiService().start("get", `${programUrlPrefix}/${programDate}/`, null, true);
            if (result?.status) {
                programByDate[programDate] = result.data;
                return result.data;
            }
            console.warn(`[TicketResolve] ${programDate} programı alınamadı (status=false)`, result);
            return null;
        })().catch((err) => {
            console.warn(`[TicketResolve] ${programDate} programı alınırken hata`, err);
            return null;
        }).finally(() => {
            delete programInFlight[programDate];
        });
    }
    return programInFlight[programDate];
}

/**
 * Program servisinden kartı bulur. ÖNEMLİ: eski kod bunu `await` etmiyordu, o yüzden
 * bugünün bülteninde olmayan hiçbir kart çözülemiyordu.
 */
export const getRacesFromProgram = async (programDate: string, cardId: any, keyword?: string): Promise<any> => {
    const data = await fetchProgramForDate(programDate);
    if (!data) {
        return null;
    }
    if (keyword) {
        const byKeyword = data.find((h: any) => h.keyword?.toLowerCase() === keyword);
        if (byKeyword) {
            return byKeyword;
        }
    }
    if (cardId == null) {
        return null;
    }
    const byId = String(cardId).includes('-')
        ? data.find((h: any) => h._id === cardId)
        : data.find((h: any) => String(h.card_id) === String(cardId));
    if (!byId) {
        console.warn(`[TicketResolve] ${programDate} programında kart bulunamadı (card_id=${cardId}, keyword=${keyword})`, {
            keywords: data.map((h: any) => h.keyword),
        });
    }
    return byId ?? null;
};

/** Resmî sonuç kaydında AGF alanları YOK; bültendeki değerler korunuyor. */
function mergeAgfFromPrevious(officialRun: any, previousRun: any): any {
    const previousHorses = new Map<string, any>(
        ((previousRun?.horses ?? []) as any[]).map((h: any) => [String(h.no), h])
    );
    return {
        ...officialRun,
        is_finished: true,
        horses: (officialRun.horses ?? []).map((h: any) => {
            const previous = previousHorses.get(String(h.no));
            if (!previous) {
                return h;
            }
            return {
                ...h,
                AGF1: h.AGF1 ?? previous.AGF1,
                AGF2: h.AGF2 ?? previous.AGF2,
                agf_ratio1: h.agf_ratio1 ?? previous.agf_ratio1,
                agf_ratio2: h.agf_ratio2 ?? previous.agf_ratio2,
                agf_sort_no1: h.agf_sort_no1 ?? previous.agf_sort_no1,
                agf_sort_no2: h.agf_sort_no2 ?? previous.agf_sort_no2,
            };
        }),
    };
}

export type ResolvedCard = {
    /** Koşu no -> koşu kaydı. Boşsa kart hiçbir kaynaktan çözülemedi. */
    runsByNo: Record<string, any>;
    /** Kartın kendisi (canlı, program ya da resmî kaynaktan). */
    hippodrome: any;
};

/**
 * TEK bir kart için canlı -> program -> resmî zincirini çalıştırır. Hata durumunda boş
 * döner; bir kartın çözülememesi diğer kartları/bacakları etkilemez.
 */
export async function resolveCard(card: string, cardId: any): Promise<ResolvedCard> {
    const empty: ResolvedCard = {runsByNo: {}, hippodrome: null};
    try {
        const date = parseCardDate(card);
        const keyword = rowHippodromeKeyword(card);
        const runsByNo: Record<string, any> = {};

        await ensureOfficialResults([date]);

        let hippodrome = getDailyRaces().find((h: any) => (cardId != null
            ? String(h.card_id) === String(cardId) || h._id === cardId
            : h.keyword?.toLowerCase() === keyword));

        if (!hippodrome?.runs) {
            hippodrome = await getRacesFromProgram(toProgramDate(date), cardId ?? card, keyword);
        }

        if (hippodrome?.runs) {
            for (const run of hippodrome.runs) {
                runsByNo[String(run.no)] = {...run, is_finished: false, hippodrome: hippodrome.hippodrome};
            }
        }

        // Resmî sonuç koşuyu EZİYOR (sonuç sırası yalnız onda var) ama AGF'si yok.
        // Kart `.keyword` ile eşleşiyor: `id.split('-')` ayrıştırması güvenilir değil.
        const officialForDate = OfficialResults[date];
        const officialHippodrome = officialForDate?.find((el: any) =>
            el.keyword?.toLowerCase() === keyword || String(el.id ?? '').split('-')[1]?.toLowerCase() === keyword);
        if (officialHippodrome?.runs) {
            hippodrome = hippodrome ?? officialHippodrome;
            for (const run of officialHippodrome.runs) {
                runsByNo[String(run.no)] = {
                    ...mergeAgfFromPrevious(run, runsByNo[String(run.no)]),
                    hippodrome: officialHippodrome.hippodrome ?? hippodrome?.hippodrome,
                };
            }
        }

        if (!hippodrome) {
            console.warn(`[TicketResolve] Kart hiçbir kaynaktan çözülemedi: ${card} (card_id=${cardId})`);
            return empty;
        }

        return {runsByNo, hippodrome};
    } catch (e) {
        console.warn(`[TicketResolve] ${card} kartı çözülürken hata`, e);
        return empty;
    }
}

/**
 * Biletin TÜM bacakları için kartları çözer. Aynı kart birden çok bacakta geçse de bir
 * kez istenir; bir kartın hatası diğerlerini bloklamaz.
 */
export async function resolveTicketCards(legs: NormalizedLeg[]): Promise<Map<string, ResolvedCard>> {
    const cards = new Map<string, any>();
    legs.forEach((leg) => {
        if (leg.card && !cards.has(leg.card)) {
            cards.set(leg.card, leg.cardId);
        }
    });

    const resolved = new Map<string, ResolvedCard>();
    await Promise.all(Array.from(cards.entries()).map(async ([card, cardId]) => {
        resolved.set(card, await resolveCard(card, cardId));
    }));
    return resolved;
}
